import { useUser } from "@supabase/auth-helpers-react";
import { Lock } from "lucide-react";
import { useUserById } from "@/hooks/useUserById";
import CustomAvatar from "./CustomAvatar";
import {
  calculateLevelAndProgress,
  USER_RATING_LEVELS,
} from "@/utils/calculateLevel";
import { useQuery } from "@tanstack/react-query";
import { fetchUsersByLevel } from "@/lib/community";
import { cn } from "@/lib/utils";
import { CurrentLevelInfo } from "./CurrentLevelInfo";
import { useTranslation } from "react-i18next";

export function ProfileLevels() {
  const currentUser = useUser();
  const { t } = useTranslation();
  const user = useUserById(currentUser.id);
  const score = user?.user_rating?.score ?? 0;
  const { level } = calculateLevelAndProgress(score);
  const { data: usersByLevel, isLoading } = useQuery({
    queryKey: ["users_by_level"],
    queryFn: fetchUsersByLevel,
  });

  const totalUsers =
    usersByLevel?.reduce((sum, lvl) => sum + lvl.users_count, 0) ?? 0;
  const getPercent = (lvl: number) => {
    if (!usersByLevel || !totalUsers) return "-";
    const found = usersByLevel.find((item) => item.level === lvl);
    if (!found) return "0%";
    return Math.round((found.users_count / totalUsers) * 100) + "%";
  };

  return (
    <div className="w-full flex flex-col md:flex-row items-center gap-6 md:gap-12 rounded-xl border p-6">
      <div className="flex flex-col items-center">
        <div className="relative">
          <CustomAvatar
            userData={user}
            size="xl"
            showProgress
            className="mx-auto"
          />
        </div>
        <div className="mt-2 text-xl font-bold">{user?.username}</div>
        <CurrentLevelInfo score={score} />
      </div>
      <div className="flex-1 flex flex-col justify-center items-center md:items-start mt-4 md:mt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 w-full">
          <div className="flex flex-col gap-y-3">
            {USER_RATING_LEVELS.filter(
              (lvl) => lvl.level <= Math.ceil(USER_RATING_LEVELS.length / 2)
            ).map((lvl) => (
              <div key={lvl.level} className="flex items-center gap-4">
                <span
                  className={cn(
                    "flex items-center justify-center w-10 h-10 text-xl rounded-full font-bold transition-colors",
                    lvl.level === level
                      ? "bg-primary text-primary-foreground"
                      : lvl.level < level
                      ? "bg-primary/10 text-primary"
                      : "bg-gray-200 text-gray-400"
                  )}
                >
                  {lvl.level > level ? <Lock className="w-6 h-6" /> : lvl.level}
                </span>
                <div>
                  <span
                    className={cn(
                      "font-semibold text-lg",
                      lvl.level === level
                        ? "text-primary"
                        : lvl.level < level
                        ? "text-primary"
                        : "text-gray-500"
                    )}
                  >
                    {t("community.profileLevels.level") +
                      " " +
                      lvl.level +
                      " - " +
                      t(`community.userLevels.${lvl.level}`)}
                  </span>
                  {lvl.unlock && (
                    <span className="ml-2 text-base text-blue-600 underline cursor-pointer">
                      {lvl.unlock}
                    </span>
                  )}
                  <div className="text-base text-gray-400">
                    {isLoading ? "..." : getPercent(lvl.level)}{" "}
                    {t("community.profileLevels.ofMembers")}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-y-3">
            {USER_RATING_LEVELS.filter(
              (lvl) => lvl.level > Math.ceil(USER_RATING_LEVELS.length / 2)
            ).map((lvl) => (
              <div key={lvl.level} className="flex items-center gap-4">
                <span
                  className={cn(
                    "flex items-center justify-center w-10 h-10 text-xl rounded-full font-bold transition-colors",
                    lvl.level === level
                      ? "bg-primary text-primary-foreground"
                      : lvl.level < level
                      ? "bg-primary/10 text-primary"
                      : "bg-gray-200 text-gray-400"
                  )}
                >
                  {lvl.level > level ? <Lock className="w-6 h-6" /> : lvl.level}
                </span>
                <div>
                  <span
                    className={cn(
                      "font-semibold text-lg",
                      lvl.level === level
                        ? "text-primary"
                        : lvl.level < level
                        ? "text-primary"
                        : "text-gray-500"
                    )}
                  >
                    {t("community.profileLevels.level") +
                      " " +
                      lvl.level +
                      " - " +
                      t(`community.userLevels.${lvl.level}`)}
                  </span>
                  {lvl.unlock && (
                    <span className="ml-2 text-base text-blue-600 underline cursor-pointer">
                      {lvl.unlock}
                    </span>
                  )}
                  <div className="text-base text-gray-400">
                    {isLoading ? "..." : getPercent(lvl.level)}{" "}
                    {t("community.profileLevels.ofMembers")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
